 
import { fetchAuthSession, signOut } from "aws-amplify/auth";

// Types
interface MessageErrorParams {
  msg?: string;
  code?: number | string;
  error?: Error | { msg_fail?: string } | string;
  status?: number;
  body?: unknown;
  method?: string;
}

interface ErrorNotification {
  message: string;
  requestCode?: number | string;
  requestMessage?: string;
  backCodeError?: number | string;
  requestBody?: unknown;
  isAborted?: boolean;
  messageComplete?: string;
}

export interface ApiResponse<T = unknown> {
  data: T | null;
  notifications: ErrorNotification;
}

interface RequestOptions extends RequestInit {
  method?: string;
  body?: BodyInit | null;
  headers?: HeadersInit;
}

type AdapterFunction<T, R> = (data: T) => R | Promise<R>;

/**
 * Generates a standardized error message based on the error type and status
 */
export function getMessageError({
  msg,
  code,
  error,
  status,
  body,
  method,
}: MessageErrorParams): ErrorNotification {
  // Network error - service not found
  if (error && error.toString() === "TypeError: Failed to fetch") {
    return {
      message: `Error. Servicio no encontrado ${method}, código 404`,
      requestCode: 404,
      requestMessage: "Not found",
    };
  }

  // Internal server error
  if (status === 500) {
    return {
      message: `Error de servicio ${method}, código ${code || status}`,
      backCodeError: code,
      requestCode: status,
      requestMessage: msg || "No especificado",
      requestBody: body,
    };
  }

  // Authentication/Authorization errors
  if (status === 401 || status === 403) {
    return {
      message: "Se requiere renovar la sesión",
      requestCode: status,
      requestMessage: "Se requiere renovar la sesión",
    };
  }

  // Generic error handling
  const errorObj = error as { msg_fail?: string } | undefined;
  const errorMessage = errorObj?.msg_fail?.split("}: ")[1] || String(error);

  return {
    message: errorMessage,
    messageComplete: errorObj?.msg_fail || String(error),
    backCodeError: code,
    requestCode: status,
    requestMessage: msg || "No especificado",
    requestBody: body,
  };
}

/**
 * Parses errors from fetch responses
 */
export const parseError = async <R = unknown>(
  error: Error & { name?: string },
  options: RequestOptions
): Promise<ApiResponse<R>> => {
  const stringError = error?.toString();

  // Handle AbortError specially
  if (error.name === "AbortError") {
    return {
      data: null as R,
      notifications: {
        message: "Request aborted",
        requestCode: "ABORTED",
        isAborted: true,
      },
    };
  }

  // Try to parse JSON error
  if (stringError?.includes("Error: {")) {
    try {
      return JSON.parse(stringError.replace("Error: ", "")) as ApiResponse<R>;
    } catch {
      // If parsing fails, return generic error
    }
  }

  return {
    data: null as R,
    notifications: getMessageError({ error, method: options.method }),
  };
};

/**
 * Adds authentication headers from AWS Amplify v6 session
 */
export const addAuthHeaders = async (): Promise<Record<string, string>> => {
  let accessToken: string | null = null;

  try {
    const session = await fetchAuthSession();

    // In AWS Amplify v6, tokens are in session.tokens
    if (session.tokens?.accessToken) {
      accessToken = session.tokens.accessToken.toString();
    }
  } catch {
    // If there's an error fetching the session, sign out the user
    await signOut();
  }

  const headers: Record<string, string> = {};

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
    headers["token-user"] = accessToken;
  }

  return headers;
};

/**
 * Generates unique request tracking headers
 */
export const addRequestHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    "X-Request-ID": crypto.randomUUID(),
    "X-Correlation-ID": crypto.randomUUID(),
    "x-Client-Name": "augur",
  };

  // Get user profile from localStorage if available
  try {
    const requiredStr = localStorage.getItem("required");
    if (requiredStr) {
      const required = JSON.parse(requiredStr);

      // Remove unnecessary properties for the header
      // cspell:ignore modulos
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { modulos, permisos, visibilidad, ...cleanRequired } = required;

      if (Object.keys(cleanRequired).length > 0) {
        headers["X-User-Profile"] = JSON.stringify(cleanRequired);

        if (cleanRequired.colaborador_id) {
          headers["X-User-ID"] = cleanRequired.colaborador_id;
        }
      }
    }
  } catch {
    // Silently ignore localStorage errors
  }

  return headers;
};

/**
 * Makes an authenticated HTTP request with standardized error handling
 *
 * @param url - The URL to fetch
 * @param options - Fetch options (method, headers, body, etc.)
 * @param adapter - Optional function to transform the response data
 * @returns Promise with data and notifications
 *
 * @example
 * ```ts
 * const response = await request('/api/users', {
 *   method: 'GET',
 *   headers: { 'Content-Type': 'application/json' }
 * });
 * ```
 */
export default async function request<T = unknown, R = T>(
  url: string,
  options: RequestOptions = {},
  adapter?: AdapterFunction<T, R>
): Promise<ApiResponse<R>> {
  try {
    const authHeaders = await addAuthHeaders();
    const requestHeaders = addRequestHeaders();

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        ...authHeaders,
        ...requestHeaders,
      },
    });

    // Handle authentication errors
    if (response.status === 401 || response.status === 403) {
      await signOut();
    }

    // Handle no content response
    if (response.status === 204) {
      return {
        data: null,
        notifications: {
          message: "No hay contenido",
          backCodeError: undefined,
          requestCode: response.status,
        },
      };
    }

    // Handle error responses
    if (response.status !== 200 && response.status !== 201) {
      const errorBody = await response.json();
      const { msg, code, error } = errorBody;

      throw new Error(
        JSON.stringify({
          data: null,
          notifications: getMessageError({
            msg,
            code,
            error,
            status: response.status,
            body: options.body,
            method: options.method,
          }),
        })
      );
    }

    // Parse successful response
    const responseBody = await response.json();
    const { data, code, msg } = responseBody;

    // Apply adapter if provided
    const transformedData = adapter ? await adapter(data) : data;

    return {
      data: transformedData as R,
      notifications: {
        message: msg,
        backCodeError: code,
        requestCode: response.status,
      },
    };
  } catch (error) {
    return parseError<R>(error as Error, options);
  }
}

